/**
 * useAgentStateMachine — Controls the 7-step agentic optimization flow.
 *
 * States:
 * 1. ANALYSIS     — Show match score + gaps
 * 2. CONSENT      — Ask permission to rewrite
 * 3. OPTIMIZATION — Stream rewrite to canvas
 * 4. REFINEMENT   — Ask for feedback
 * 5. TEMPLATE     — Template selection
 * 6. FOLLOWUP     — Offer cover letter
 * 7. FULFILLMENT  — Save/download/email actions
 *
 * This hook coordinates between the chat (left panel) and canvas (right panel),
 * posting appropriate messages and dispatching canvas state changes.
 */

import { useCallback, useRef } from 'react';

import { useCanvas } from './useCanvas';
import { waitForCanvasTypewriter } from './useCanvasTypewriter';
import {
  type JobContext,
  type ResumeData,
  type AnalysisResult,
} from './CanvasProvider';
import { analyzeATSCompliance } from '../../../utils/atsComplianceAnalyzer';
import { optimizeSection, type JobContext as OptJobContext } from '../../../services/sectionOptimizationService';
import { ledger } from '../../../services/creditLedgerService';
import { saveAgentArtifacts } from '../../../services/applicationTrackingService';
import { generateLetter, buildFallbackLetter } from '../agentLetters';
import {
  normalizeDocumentLocale,
  referencesFooter,
  sectionDisplayTitle,
} from '../utils/documentLanguage';
import { buildAnalysisMessage } from '../utils/agentWorkflowI18n';
import {
  applicationNoteReadyMessage,
  coverLetterReadyMessage,
  draftingCoverLetterWithTabMessage,
  refineCoverLetterPrompt,
  refineSectionPrompt,
} from '../utils/agentEngagementI18n';
import i18n from '../../../i18n';
import { parseCanvasSectionKeyFromText, finalizeResumeSectionContent, buildTargetRoleHeadline, buildPersonalSectionContent } from './canvasSectionUtils';
import {
  parseResumeSections,
  sortSectionsByCanonicalOrder,
  normalizeSectionTitles,
} from '../../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import { TEMPLATE_LIBRARY } from '../../dashboard/components/AIResumeOptimizer/ResultsStep';
import { normalizeResumeText } from '../../../utils/pdfTextSanitizer';
import type { UserProfile } from '../../../types/userProfile';

const t = i18n.t.bind(i18n);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: AgentAction[];
}

export interface AgentAction {
  id: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
}

export interface UseAgentStateMachineOptions {
  onMessage: (message: AgentMessage) => void;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
}

// ── Helper: flatten and clean a raw skills array or string ──────────────────
// profile.skills may contain category-prefixed blobs like:
//   "Languages & Frameworks: Java, Python, Go, BASH"
// This strips the category prefix and returns individual clean skills.
function flattenSkills(rawSkills: string[]): string[] {
  const cleaned: string[] = [];
  for (const raw of rawSkills) {
    // Split on comma, bullet, pipe, newline
    const tokens = raw.split(/[,•|\n]+/);
    for (const tok of tokens) {
      // Strip leading category label  (e.g. "Cloud Technologies : " or "AI/ML Technologies : ")
      const stripped = tok.replace(/^[^:]{1,50}:\s*/, '').trim();
      const val = stripped || tok.trim();
      // Filter: must be 2–55 chars, not a section header, not a metadata line
      if (
        val.length >= 2 && val.length <= 55 &&
        !/^(title|current role|years of|industry|location|summary|experience|education|skills|profile|resume|headline|contact|phone|email|linkedin|github|portfolio)\b/i.test(val) &&
        !/^─+$/.test(val) && !/^\d+$/.test(val)
      ) {
        cleaned.push(val);
      }
    }
  }
  return [...new Set(cleaned)].filter(s => s.length > 0);
}

function parseResumeToData(text: string): ResumeData {
  const sections = sortSectionsByCanonicalOrder(
    normalizeSectionTitles(parseResumeSections(normalizeResumeText(text)))
  );

  const data: ResumeData = {
    name: '',
    skills: [],
    experience: [],
    education: [],
    certifications: [],
    projects: [],
  };

  for (const sec of sections) {
    const title = sec.title.toUpperCase();
    const content = sec.content || '';
    const lines = content.split('\n').filter((l) => l.trim());

    // ── Contact / Personal Information ──
    if (title.includes('CONTACT') || title.includes('PERSONAL') || title.includes('NAME') || title === 'HEADER') {
      if (lines[0] && !lines[0].includes('@')) data.name = lines[0].trim();
      data.contact = {};
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.includes('@') && !data.contact.email) data.contact.email = trimmed;
        else if (/\+?\d[\d\s\-]{8,}/.test(trimmed) && !data.contact.phone) data.contact.phone = trimmed;
        else if (trimmed.toLowerCase().includes('linkedin')) data.contact.linkedin = trimmed;
        else if (trimmed.toLowerCase().includes('github')) data.contact.github = trimmed;
        else if (!data.contact.location && !trimmed.includes('@') && trimmed.length > 3 && trimmed.length < 100) {
          // Location heuristic: contains comma or known city patterns
          if (trimmed.includes(',') || /bengaluru|mumbai|delhi|india|usa|uk/i.test(trimmed)) {
            data.contact.location = trimmed;
          }
        }
      }
    }
    // ── Professional Summary ──
    else if (title.includes('SUMMARY') || title.includes('OBJECTIVE') || title.includes('PROFILE') ||
             (title.includes('PROFESSIONAL') && !title.includes('EXPERIENCE'))) {
      data.summary = content.trim();
    }
    // ── Skills ──
    else if (title.includes('SKILL') || title.includes('TECHNICAL') || title.includes('COMPETENC')) {
      const skills = content
        .split(/[,•|\n]/)
        .flatMap((s) => s.split(':').pop()?.split(/[,;]/) || [s])
        .map((s) => s.trim())
        .filter((s) => s.length > 1 && s.length < 60 && !/^\d+$/.test(s));
      data.skills = [...new Set(skills)]; // dedupe
    }
    // ── Work Experience ──
    else if (title.includes('EXPERIENCE') || title.includes('EMPLOYMENT') || title.includes('WORK') || title.includes('CAREER')) {
      type ExpEntry = NonNullable<typeof data.experience>[number];
      let currentExp: ExpEntry | null = null;

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Bullet point → add to current experience
        if (/^[•\-–▪]/.test(trimmed)) {
          if (currentExp) {
            currentExp.bullets.push(trimmed.replace(/^[•\-–▪]\s*/, ''));
          }
          continue;
        }

        // Date line (standalone dates)
        const dateMatch = trimmed.match(/(\w+\s+\d{4}|\d{4})\s*[-–to]+\s*(\w+\s+\d{4}|\d{4}|present|current)/i);
        if (dateMatch && currentExp && !currentExp.startDate) {
          currentExp.startDate = dateMatch[1];
          currentExp.endDate = /present|current/i.test(dateMatch[2]) ? undefined : dateMatch[2];
          continue;
        }

        // Title/Company line (contains | or @ or looks like a job entry)
        const parts = trimmed.split(/[|@–]/);
        if (parts.length >= 2 || /\b(manager|engineer|developer|lead|director|analyst|specialist|consultant)\b/i.test(trimmed)) {
          // Save previous experience
          if (currentExp && currentExp.title) data.experience!.push(currentExp);
          
          currentExp = {
            title: parts[0]?.trim() || trimmed,
            company: parts[1]?.trim() || '',
            location: parts[2]?.trim(),
            startDate: '',
            bullets: [],
          };
          
          // Check if dates are in the same line
          const inlineDate = trimmed.match(/(\w+\s+\d{4}|\d{4})\s*[-–to]+\s*(\w+\s+\d{4}|\d{4}|present|current)/i);
          if (inlineDate) {
            currentExp.startDate = inlineDate[1];
            currentExp.endDate = /present|current/i.test(inlineDate[2]) ? undefined : inlineDate[2];
          }
        } else if (currentExp && !currentExp.company && trimmed.length < 100) {
          // Company name on a separate line
          currentExp.company = trimmed;
        }
      }
      if (currentExp && currentExp.title) data.experience!.push(currentExp);
    }
    // ── Education ──
    else if (title.includes('EDUCATION') || title.includes('ACADEMIC') || title.includes('QUALIFICATION')) {
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.length < 5) continue;
        
        const yearMatch = trimmed.match(/\d{4}/);
        const parts = trimmed.split(/[–\-,]/);
        
        data.education!.push({
          degree: parts[0]?.replace(/\d{4}/g, '').trim() || trimmed,
          institution: parts[1]?.replace(/\d{4}/g, '').trim() || '',
          year: yearMatch?.[0] || '',
        });
      }
    }
    // ── Certifications ──
    else if (title.includes('CERTIFICATION') || title.includes('LICENSE')) {
      data.certifications = lines
        .map((l) => l.trim().replace(/^[•\-–]\s*/, ''))
        .filter((l) => l.length > 3);
    }
    // ── Projects ──
    else if (title.includes('PROJECT')) {
      let currentProj: { name: string; description: string; technologies?: string[] } | null = null;

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        
        if (/^[•\-–]/.test(trimmed)) {
          if (currentProj) {
            currentProj.description += (currentProj.description ? ' ' : '') + trimmed.replace(/^[•\-–]\s*/, '');
          }
        } else {
          if (currentProj) data.projects!.push(currentProj);
          currentProj = { name: trimmed, description: '' };
        }
      }
      if (currentProj) data.projects!.push(currentProj);
    }
    // ── Awards / Achievements (extract as metrics) ──
    else if (title.includes('AWARD') || title.includes('ACHIEVEMENT') || title.includes('HONOR')) {
      data.metrics = lines
        .filter((l) => l.trim())
        .slice(0, 4)
        .map((l) => ({ label: l.trim().replace(/^[•\-–]\s*/, '').slice(0, 40), value: '✓' }));
    }
  }

  // Fallback: extract name from first meaningful line if not found
  if (!data.name && text.length > 10) {
    const firstLine = text.split('\n').find((l) => l.trim() && l.trim().length < 50 && !l.includes('@'));
    if (firstLine) data.name = firstLine.trim();
  }

  return data;
}

/**
 * Structured resume from plain text — same ATS parsing chain as single-optimize
 * (`normalizeResumeText` → `parseResumeSections` → `normalizeSectionTitles` → canonical order).
 * Use when `getBestResumePlainTextForAgent` returns a full document (upload + sections).
 */
export function resumePlainTextToResumeData(text: string): ResumeData {
  return parseResumeToData(text);
}

// ── Helper: Convert UserProfile to ResumeData (uses pre-parsed structured data) ──

export function profileToResumeData(
  profile: UserProfile | null,
  opts?: { authEmail?: string; authPhone?: string },
): ResumeData | null {
  if (!profile) return null;

  const hasStructuredData =
    profile.experiences?.length > 0 ||
    profile.education?.length > 0 ||
    profile.professionalSummary ||
    profile.skills?.length > 0;

  if (!hasStructuredData) return null;

  return {
    name: profile.displayName || '',
    headline: profile.headline || profile.currentRole || undefined,
    contact: {
      email: opts?.authEmail || undefined,
      phone: opts?.authPhone || undefined,
      location: profile.location || undefined,
      linkedin: profile.linkedinUrl || undefined,
      github: profile.githubUrl || undefined,
      portfolio: profile.portfolioUrl || undefined,
    },
    summary: profile.professionalSummary || undefined,
    skills: flattenSkills(profile.skills || []),
    experience: (profile.experiences || []).map((exp) => ({
      title: exp.title,
      company: exp.company,
      location: exp.location,
      startDate: exp.startDate || '',
      endDate: exp.current ? undefined : exp.endDate || undefined,
      bullets: exp.bullets || [],
    })),
    education: (profile.education || []).map((edu) => ({
      degree: edu.degree,
      institution: edu.institution,
      year: edu.year || '',
      gpa: edu.gpa,
    })),
    certifications: (profile.certifications || []).map((c) => c.name),
    projects: (profile.projects || []).map((p) => ({
      name: p.name,
      description: p.description,
      technologies: p.technologies,
    })),
  };
}

// ── Helper: Analyze resume against job ────────────────────────────────────────

function analyzeResumeVsJob(
  resumeText: string,
  job: JobContext,
): AnalysisResult {
  const ats = analyzeATSCompliance(resumeText, {
    skills: job.skills ?? [],
    description: job.description ?? '',
    requirements: job.requirements ?? [],
  });

  const skills = job.skills ?? [];
  const lower = resumeText.toLowerCase();
  const matched = skills.filter((s) => lower.includes(s.toLowerCase()));
  const missing = skills.filter((s) => !lower.includes(s.toLowerCase()));

  // If the recommendation engine already computed a score for this job, trust it —
  // it uses Pinecone vector similarity + 6 signals and is far more accurate than
  // the local ATS heuristic (which collapses to 0 when skills[] is empty).
  const localScore = skills.length > 0
    ? Math.round(ats.overallScore * (matched.length / skills.length))
    : Math.round(ats.overallScore);
  const matchScore = job.precomputedMatchScore !== undefined
    ? Math.round(job.precomputedMatchScore)
    : localScore;

  // Smart template selection based on job type and keywords
  const titleLower = (job.title || '').toLowerCase();
  const descLower = (job.description || '').toLowerCase();
  const combined = `${titleLower} ${descLower}`;
  
  const isManagement = /executive|director|vp |vice president|head of|chief|cto|cfo|ceo|manager|lead|principal|senior manager/i.test(combined);
  const isTechnical = /engineer|developer|architect|devops|sre|software|backend|frontend|fullstack|data scientist|ml engineer|platform/i.test(combined);
  const isProduct = /product manager|product owner|pm |program manager/i.test(combined);
  
  // Map to real TEMPLATE_LIBRARY ids
  const suggestedTemplate: string = 
    isManagement ? 'executive' :
    isTechnical ? 'navy-tech' :
    isProduct ? 'modern-blue' :
    'ats-optimized';

  return {
    matchScore,
    gaps: missing.slice(0, 5),
    strengths: matched.slice(0, 5),
    suggestedTemplate,
    keywordHits: matched.slice(0, 12),
    keywordMisses: missing.slice(0, 12),
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAgentStateMachine({ onMessage, onStreamStart, onStreamEnd }: UseAgentStateMachineOptions) {
  const { state, dispatch, startStreaming, appendStream, stopStreaming, setPhase, prepareOptimization, startOptimization } = useCanvas();
  const abortRef = useRef<AbortController | null>(null);
  const changelogRef = useRef<string[]>([]);
  // Keep a ref to the latest state to avoid stale closures in async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // ── Phase 1: Start Analysis ─────────────────────────────────────────────────
  const startAnalysis = useCallback(
    (job: JobContext, resumeText: string, preloadedResumeData?: ResumeData | null) => {
      // Use pre-loaded structured data if available, otherwise parse from raw text
      const resumeData = preloadedResumeData ?? parseResumeToData(resumeText);

      // Prepare context WITHOUT opening the canvas — canvas opens only on user confirmation
      prepareOptimization(job, resumeData, resumeText);

      const analysis = analyzeResumeVsJob(resumeText, job);
      dispatch({ type: 'SET_ANALYSIS', analysis });
      dispatch({ type: 'SET_TEMPLATE', templateId: analysis.suggestedTemplate });

      const { content, yesRewriteLabel, pickDifferentLabel } = buildAnalysisMessage(job, analysis, resumeData);

      onMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content,
        actions: [
          {
            id: 'yes-rewrite',
            label: yesRewriteLabel,
            variant: 'primary',
            onClick: () => {
              // Snapshot current sections for diff view, then open canvas
              dispatch({ type: 'SNAPSHOT_ORIGINAL_SECTIONS' });
              startOptimization(job, resumeData, resumeText);
              const origData = stateRef.current.originalResumeData ?? resumeData;
              const docLocale = normalizeDocumentLocale(stateRef.current.documentLocale);
              const sectionsToOptimize = ['summary', 'experience', 'skills'];
              if (origData?.projects && origData.projects.length > 0) {
                sectionsToOptimize.push('projects');
              }
              dispatch({
                type: 'SET_PENDING_SECTIONS',
                sections: sectionsToOptimize.map((s) => sectionDisplayTitle(s, docLocale)),
              });
              void startOptimizationPhase(sectionsToOptimize);
            },
          },
          {
            id: 'back-jobs',
            label: pickDifferentLabel,
            variant: 'secondary',
            onClick: () => {
              dispatch({ type: 'BACK_TO_LIST' });
            },
          },
        ],
      });
    },
    [dispatch, onMessage, prepareOptimization, startOptimization],
  );

  // ── Phase 2: Consent (kept for manual trigger only, auto-bypassed in startAnalysis) ──
  const proceedToConsent = useCallback(() => {
    setPhase('consent');
    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: `Rewriting all sections for ATS optimization — watch the canvas`,
    });
    const origData = stateRef.current.originalResumeData;
    const docLocale = normalizeDocumentLocale(stateRef.current.documentLocale);
    const sectionsToOptimize = ['summary', 'experience', 'skills'];
    if (origData?.projects && origData.projects.length > 0) {
      sectionsToOptimize.push('projects');
    }
    dispatch({
      type: 'SET_PENDING_SECTIONS',
      sections: sectionsToOptimize.map((s) => sectionDisplayTitle(s, docLocale)),
    });
    void startOptimizationPhase(sectionsToOptimize);
  }, [setPhase, onMessage, dispatch]);

  // ── Phase 3: Optimization (Streaming) ───────────────────────────────────────
  const startOptimizationPhase = useCallback(
    async (sections: string[]) => {
      setPhase('optimization');
      onStreamStart?.();

      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;

      // Debit credits for full agentic resume optimisation
      void ledger.resumeOptimize({
        job_id: stateRef.current.job?.id,
        job_title: stateRef.current.job?.title,
        sections,
      });

      const job = stateRef.current.job;

      // Build section content from MULTIPLE sources (prioritized):
      // 1. Parse raw resume text (if available)
      // 2. Build from structured resumeData (pre-loaded from profile)
      
      // Use ref to get latest state values (avoids stale closure in async callback).
      // IMPORTANT: use originalResumeData (full profile data) for context building.
      const rawText = stateRef.current.rawResumeText || '';
      const originalData = stateRef.current.originalResumeData;
      // Fallback typed data from original profile (never from cleared canvas sections)
      const resumeData = originalData;
      
      console.log('[AgentStateMachine] Starting optimization phase:', {
        rawTextLength: rawText.length,
        hasOriginalData: !!originalData,
        hasExperience: originalData?.experience?.length || 0,
        hasSummary: !!originalData?.summary,
        hasSkills: originalData?.skills?.length || 0,
        sections,
      });

      // fullResumeSummary: prefer the original profile data (rich context), fall back to rawText
      const fullResumeSummary =
        rawText.length > 50
          ? rawText.slice(0, 800).replace(/\s+/g, ' ').trim()
          : [
              originalData?.headline,
              ...(originalData?.experience?.slice(0, 2).map((e) => `${e.title} at ${e.company}`) ?? []),
              originalData?.skills?.slice(0, 12).join(', '),
            ]
              .filter(Boolean)
              .join(' | ')
              .slice(0, 600)
              .trim() || undefined;

      // Always build context from originalData so skills/headline are available
      // even though resumeData (canvas) has been cleared for the "AI writing" effect.
      const docLocale = normalizeDocumentLocale(stateRef.current.documentLocale);
      const baseResumeCtx = {
        candidateName: originalData?.name ?? resumeData?.name,
        headline: originalData?.headline ?? resumeData?.headline,
        skills: (originalData?.skills?.length ? originalData.skills : resumeData?.skills) ?? [],
        fullResumeSummary: fullResumeSummary || undefined,
        outputLanguage: docLocale,
        preserveExistingContent: true,
        experience: (originalData?.experience ?? resumeData?.experience)?.map((e) => ({
          title: e.title,
          company: e.company,
          location: e.location,
          startDate: e.startDate,
          endDate: e.endDate,
          bullets: e.bullets,
        })),
        experienceYears: originalData?.experience?.length
          ? Math.max(1, originalData.experience.length * 2)
          : undefined,
      };

      // Prefer canvas sections when the user is viewing a saved/optimized resume.
      const sectionContentMap: Record<string, string> = {};
      for (const row of stateRef.current.sections ?? []) {
        const key = parseCanvasSectionKeyFromText(row.title);
        const body = row.content?.trim() ?? '';
        if (key && body.length >= 10) {
          sectionContentMap[key] = body;
        }
      }

      // Fallback: Build section content from the ORIGINAL profile data
      const buildSummaryFromData = (): string => {
        const src = originalData ?? resumeData;
        if (src?.summary) return src.summary;
        // Build a minimal seed using ONLY the candidate's profile facts — no JD content.
        // Mixing JD requirements into the seed causes the LLM to rewrite the job description
        // instead of the candidate's actual background.
        const headline = src?.headline || '';
        const topSkills = flattenSkills(src?.skills ?? []).slice(0, 6).join(', ');
        const topExp = (src?.experience ?? []).slice(0, 1).map(e => `${e.title} at ${e.company}`).join('');
        return [
          headline ? `${headline} with extensive industry experience.` : `Experienced professional.`,
          topExp ? `Most recently: ${topExp}.` : '',
          topSkills ? `Key skills include ${topSkills}.` : '',
        ].filter(Boolean).join(' ').trim();
      };

      const buildExperienceFromData = (): string => {
        const src = originalData ?? resumeData;
        if (!src?.experience?.length) return '';
        return src.experience.map((exp: { title: string; company: string; location?: string; startDate: string; endDate?: string; bullets?: string[]; description?: string }) => {
          const dateRange = exp.startDate
            ? `${exp.startDate} – ${exp.endDate || 'Present'}`
            : '';
          const company = exp.company?.trim() || '';
          const title = exp.title?.trim() || 'Role';
          const bullets = (exp.bullets || []).filter(Boolean);
          const lines = [
            company
              ? `**${title}** | ${company}${exp.location ? `, ${exp.location}` : ''}`
              : `**${title}**${exp.location ? ` | ${exp.location}` : ''}`,
            dateRange,
            ...bullets.map((b: string) => `• ${b}`),
            ...(bullets.length === 0 && exp.description
              ? [`• ${exp.description}`]
              : bullets.length === 0
                ? [`• Led cross-functional initiatives delivering measurable business outcomes.`]
                : []),
          ].filter(Boolean);
          return lines.join('\n');
        }).join('\n\n');
      };

      const buildSkillsFromData = (): string => {
        const src = originalData ?? resumeData;
        if (!src?.skills?.length) return '';
        const cleanSkills = flattenSkills(src.skills).filter((s) => {
          if (s.length < 2 || s.length > 50) return false;
          if (/^(title|years of|years|experience|industry|technology|cloud|languages|frameworks|tools|platform|about|profile|summary|education|contact|name|address|phone|email)$/i.test(s)) return false;
          return true;
        });
        return cleanSkills.slice(0, 35).join(', ');
      };

      const buildProjectsFromData = (): string => {
        const src = originalData ?? resumeData;
        if (!src?.projects?.length) return '';
        return src.projects.map((p) => {
          const lines = [p.name];
          if (p.description) lines.push(`• ${p.description}`);
          if (p.technologies?.length) lines.push(`Technologies: ${p.technologies.join(', ')}`);
          return lines.join('\n');
        }).join('\n\n');
      };

      // Track whether experience was AI-generated (no source data) so we can warn the user
      let experienceWasGenerated = false;

      changelogRef.current = [];

      try {
        const totalSteps = sections.length;
        let stepIdx = 0;

        for (const sectionName of sections) {
          if (ctl.signal.aborted) break;
          stepIdx++;

          const displayTitleForProgress = sectionDisplayTitle(sectionName, docLocale);
          dispatch({ type: 'SET_ACTIVE_SECTION', title: displayTitleForProgress });
          onMessage({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Optimizing **${displayTitleForProgress}** (${stepIdx}/${totalSteps})…`,
          });

          // Get original content - try parsed sections first, then build from structured data
          let originalContent = sectionContentMap[sectionName] || '';
          
          // Fallback: Build from structured resumeData
          if (!originalContent || originalContent.length < 10) {
            if (sectionName === 'summary') {
              originalContent = buildSummaryFromData();
            } else if (sectionName === 'experience') {
              originalContent = buildExperienceFromData();
            } else if (sectionName === 'skills') {
              originalContent = buildSkillsFromData();
            } else if (sectionName === 'projects') {
              originalContent = buildProjectsFromData();
            }
          }

          // Build job context for optimization/generation
          const jobCtx: OptJobContext = {
            title: stateRef.current.job?.title || '',
            company: stateRef.current.job?.company || '',
            description: stateRef.current.job?.description || '',
            skills: stateRef.current.job?.skills || [],
            requirements: stateRef.current.job?.requirements || [],
          };

          let result: { optimizedContent: string };
          const needsGeneration = !originalContent || originalContent.length < 10;

          // ── Clean skills content before sending to LLM ──
          // Profile skills may contain metadata ("Title: PM", "Years of experience", standalone
          // category-name fragments like "Cloud", "Languages &"). Strip them before the LLM sees them.
          if (sectionName === 'skills' && originalContent) {
            originalContent = originalContent
              .split('\n')
              .filter((line) => {
                const t = line.trim();
                if (!t || /^\d+$/.test(t)) return false;
                if (/^(Title|Years of experience|Years of|Industry|Technology|Cloud|Languages|Frameworks|Experience)\s*$/i.test(t)) return false;
                if (/^(Title|Years of)\s*:/i.test(t)) return false;
                if (t.endsWith('&') || t.endsWith('|')) return false;
                return true;
              })
              .join('\n')
              .trim();
          }

          if (needsGeneration) {
            // ── Experience: section_optimize refuses seeds without real employer names ──
            // Skip the LLM call entirely — write a structured fillable template to the canvas.
            if (sectionName === 'experience') {
              experienceWasGenerated = true;
              const expHeadline = originalData?.headline || jobCtx.title || 'Your Role';
              const expLocation = originalData?.contact?.location || 'Your Location';
              const topSkills = (jobCtx.skills || []).slice(0, 3).join(', ') || 'relevant technologies';
              result = {
                optimizedContent: [
                  `${expHeadline} | [Company Name] | ${expLocation} | [Start Year] – [End Year or Present]`,
                  `• [Describe a key achievement, e.g. "Launched X feature that increased Y metric by Z%"]`,
                  `• Applied expertise in ${topSkills} to deliver cross-functional projects`,
                  `• [Add another impactful responsibility or accomplishment]`,
                  `• [Quantify your impact: team size, revenue, time saved, adoption rate]`,
                ].join('\n'),
              };
              onMessage({
                id: crypto.randomUUID(),
                role: 'assistant',
                content: `No work experience found in your profile — added a fillable template to the canvas. **Click any section in the canvas to fill in your real company, dates, and achievements.**`,
              });
            } else {
              console.log(`[AgentStateMachine] Generating ${sectionName} from scratch`);
              result = await optimizeSection(
                { title: sectionDisplayTitle(sectionName, docLocale), content: '' },
                jobCtx,
                ctl.signal,
                { ...baseResumeCtx, generateMode: true },
              );
            }
          } else {
            // OPTIMIZE existing content
            console.log(`[AgentStateMachine] Optimizing ${sectionName} (${originalContent.length} chars)`);

            result = await optimizeSection(
              { title: sectionDisplayTitle(sectionName, docLocale), content: originalContent },
              jobCtx,
              ctl.signal,
              baseResumeCtx,
            );
          }

          // Sanitize + repair LLM output; fall back to profile source if truncated or placeholder-heavy
          const displayTitle = sectionDisplayTitle(sectionName, docLocale);
          const cleanContent = finalizeResumeSectionContent(
            result.optimizedContent,
            originalContent,
            sectionName,
            originalData ?? resumeData,
            jobCtx.title,
          );

          // Drop the fully rendered section into the canvas in one clean transition
          startStreaming(displayTitle);
          appendStream(cleanContent);
          await waitForCanvasTypewriter(cleanContent);
          stopStreaming();

          // Commit the plain-text optimized content directly — no typed parsing needed.
          // SectionContentWithFormatting in StreamingCanvas renders it correctly.
          dispatch({ type: 'UPSERT_SECTION', title: displayTitle, content: cleanContent });

          // Build changelog entry for this section
          if (needsGeneration) {
            changelogRef.current.push(`Generated **${displayTitle}** from scratch — tailored to ${jobCtx.title} requirements`);
          } else {
            const oldLen = originalContent.length;
            const newLen = cleanContent.length;
            const keywords = (jobCtx.skills || []).filter(s => cleanContent.toLowerCase().includes(s.toLowerCase()) && !originalContent.toLowerCase().includes(s.toLowerCase()));
            const parts: string[] = [];
            if (keywords.length > 0) parts.push(`added keywords: ${keywords.slice(0, 3).join(', ')}`);
            if (newLen > oldLen * 1.2) parts.push('expanded with more detail');
            else if (newLen < oldLen * 0.8) parts.push('condensed for clarity');
            if (sectionName === 'experience') parts.push('strengthened with action verbs and metrics');
            if (sectionName === 'summary') parts.push('aligned to target role');
            changelogRef.current.push(`Optimized **${displayTitle}** — ${parts.length > 0 ? parts.join(', ') : 'enhanced for ATS compliance'}`);
          }

          await new Promise((r) => setTimeout(r, 300));
        }

        // ── Append non-LLM ATS sections from profile data ──
        // Education, certifications, and projects are formatted locally (no LLM needed).
        // Placeholders are inserted even when profile data is missing — the user can fill them in.
        // Defined before use so it can be called in both paths below.
        const appendStaticSections = () => {
          const src = stateRef.current.originalResumeData;

          // Refresh contact/header with target-role-aligned headline (keeps real email/phone)
          if (src) {
            const alignedHeadline = buildTargetRoleHeadline(stateRef.current.job?.title, src);
            const personalContent = buildPersonalSectionContent(
              '',
              null,
              { ...src, headline: alignedHeadline },
              { enrichMissing: false },
            );
            if (personalContent.trim()) {
              dispatch({
                type: 'UPSERT_SECTION',
                title: sectionDisplayTitle('personal', docLocale),
                content: personalContent,
              });
            }
          }

          // Education — always add (profile data or fillable placeholder)
          const eduContent =
            src?.education && src.education.length > 0
              ? src.education
                  .map((edu) => {
                    const parts = [edu.degree];
                    if (edu.institution) parts.push(`— ${edu.institution}`);
                    if (edu.year) parts.push(`(${edu.year})`);
                    if (edu.gpa) parts.push(`| GPA: ${edu.gpa}`);
                    return parts.join(' ');
                  })
                  .join('\n')
              : `[Degree Name] | [Institution Name] | [Graduation Year]\n[Add more education if applicable]`;
          dispatch({ type: 'UPSERT_SECTION', title: sectionDisplayTitle('education', docLocale), content: eduContent });

          // Certifications — add only when profile has entries
          if (src?.certifications && src.certifications.length > 0) {
            const certContent = src.certifications.map((c) => `• ${c}`).join('\n');
            dispatch({ type: 'UPSERT_SECTION', title: sectionDisplayTitle('certifications', docLocale), content: certContent });
          }

          // Projects — only add if the user has real project data and it wasn't LLM-optimized already.
          // If no project data exists, fall through to the References section below.
          const hasRealProjects = src?.projects && src.projects.length > 0;
          if (!sections.includes('projects') && hasRealProjects) {
            const projContent = src!.projects!
              .map((p) => {
                const lines = [`**${p.name}**`];
                if (p.description) lines.push(`• ${p.description}`);
                if (p.technologies?.length) lines.push(`Technologies: ${p.technologies.join(', ')}`);
                return lines.join('\n');
              })
              .join('\n\n');
            dispatch({ type: 'UPSERT_SECTION', title: sectionDisplayTitle('projects', docLocale), content: projContent });
          }

          // References — always append at the end (standard ATS footer section)
          dispatch({ type: 'UPSERT_SECTION', title: sectionDisplayTitle('references', docLocale), content: referencesFooter(docLocale) });
        };

        if (experienceWasGenerated) {
          // Still append static sections (education, certifications) even when experience was AI-generated
          appendStaticSections();
          dispatch({ type: 'SET_ACTIVE_SECTION', title: '' });
          // Remind the user to personalize the AI-drafted experience
          onMessage({
            id: crypto.randomUUID(),
            role: 'assistant',
            content:
              '**Experience bullets are AI-drafted** — they are based on your headline and skills, not real job history.\n\n' +
              'To get truly ATS-ready bullets, [add your real work experience](/profile?section=experience) ' +
              'in your profile (title, company, dates, achievements), then tap **Re-optimize**.',
            actions: [
              {
                id: 'add-experience',
                label: 'Add real experience',
                variant: 'primary',
                onClick: () => {
                  window.location.href = '/profile?section=experience';
                },
              },
              {
                id: 'continue-anyway',
                label: 'Continue as-is',
                variant: 'secondary',
                onClick: () => proceedToRefinement(),
              },
            ],
          });
          return;
        }

        appendStaticSections();

        dispatch({ type: 'SET_ACTIVE_SECTION', title: '' });
        proceedToRefinement();
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          console.error('[AgentStateMachine] Optimization failed:', err);
          onMessage({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: `Something went wrong during optimization. Want to try again?`,
            actions: [
              {
                id: 'retry',
                label: 'Retry',
                variant: 'primary',
                onClick: () => startOptimizationPhase(sections),
              },
            ],
          });
        }
      } finally {
        onStreamEnd?.();
        stopStreaming();
      }
    },
    [setPhase, onMessage, onStreamStart, onStreamEnd, startStreaming, appendStream, stopStreaming, dispatch],
  );

  // ── Phase 4: Refinement ─────────────────────────────────────────────────────
  const proceedToRefinement = useCallback(() => {
    setPhase('refinement');

    // Re-score the resume using the freshly-optimised sections so the canvas
    // header and chat reflect the real post-optimisation match improvement.
    const prevAnalysis = stateRef.current.analysis;
    const currentJob = stateRef.current.job;
    let scoreImprovedMsg = '';

    if (prevAnalysis && currentJob) {
      // Concatenate all newly-written sections into a plain text blob for scoring.
      const optimizedText = (stateRef.current.sections ?? [])
        .map((s) => `${s.title}\n${s.content}`)
        .join('\n\n');

      // Strip precomputedMatchScore so the local ATS keyword-density analysis
      // (which now sees all the newly-injected keywords) is used instead of the
      // frozen pre-optimisation recommendation-engine score.
      const jobForScoring: JobContext = { ...currentJob, precomputedMatchScore: undefined };
      const newAnalysis = analyzeResumeVsJob(optimizedText, jobForScoring);

      // The baseline is the very first score (locked by the reducer in
      // originalMatchScore on first SET_ANALYSIS). Score can only go up after
      // optimisation — clamp to the baseline as floor.
      const baselineScore = stateRef.current.originalMatchScore ?? prevAnalysis.matchScore;
      const improvedScore = Math.max(baselineScore, newAnalysis.matchScore);

      dispatch({
        type: 'SET_ANALYSIS',
        analysis: { ...newAnalysis, matchScore: improvedScore },
      });

      if (improvedScore > baselineScore) {
        const delta = improvedScore - baselineScore;
        scoreImprovedMsg = `\n\n**ATS Match improved: ${baselineScore}% → ${improvedScore}% (+${delta}%)**`;
      }

      // Persist optimized resume to My Activities (best-effort, non-blocking)
      const resumeMarkdown = (stateRef.current.sections ?? [])
        .map((s) => `## ${s.title}\n${s.content}`)
        .join('\n\n');
      if (currentJob?.id && resumeMarkdown.length > 50) {
        void saveAgentArtifacts({
          jobId: String(currentJob.id),
          jobTitle: currentJob.title || 'Untitled role',
          companyName: currentJob.company || 'Unknown company',
          jobUrl: (currentJob as { url?: string }).url,
          location: (currentJob as { location?: string }).location,
          sourceChannel: ((currentJob as { source?: string }).source as
            | 'linkedin' | 'naukri' | 'indeed' | 'glassdoor' | 'company_website' | 'referral' | 'other'
            | undefined) ?? 'other',
          resumeMarkdown,
          matchScore: improvedScore,
        });
      }
    }

    const sectionList = (stateRef.current.sections ?? [])
      .filter(s => !/personal|contact|references/i.test(s.title))
      .map(s => `- **${s.title}** ✓`)
      .join('\n');

    const baselineScore = stateRef.current.originalMatchScore ?? prevAnalysis?.matchScore ?? 0;
    const currentScore = stateRef.current.analysis?.matchScore ?? baselineScore;
    const jobTitle = currentJob?.title || 'this role';
    const jobCompany = currentJob?.company || 'this company';

    const summaryLines = [
      '**Optimization complete.**',
      '',
      currentScore > baselineScore
        ? `Your resume match improved from **${baselineScore}%** to **${currentScore}%** (+${currentScore - baselineScore} points).`
        : `Your ATS match is **${currentScore}%**.`,
      '',
      `You're set for **${jobTitle}** at **${jobCompany}**.`,
      '',
      '**Sections optimized:**',
      sectionList,
    ];

    const changelogEntries = changelogRef.current;
    if (changelogEntries.length > 0) {
      summaryLines.push('', '**What changed:**');
      changelogEntries.forEach(entry => summaryLines.push(`- ${entry}`));
    }

    summaryLines.push('');
    summaryLines.push('Tap an action below, or tell me what to refine.');

    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: summaryLines.join('\n'),
      actions: [
        {
          id: 'yes-cover',
          label: 'Yes, write cover letter',
          variant: 'primary',
          onClick: () => generateCoverLetter(),
        },
        {
          id: 'reoptimize-section',
          label: 'Re-optimize a section',
          variant: 'secondary',
          onClick: () => {
            const sectionNames = (stateRef.current.sections ?? [])
              .filter(s => !/personal|contact|references/i.test(s.title))
              .map(s => s.title);

            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: `Which section would you like me to re-optimize?\n\n${sectionNames.map(s => `- **${s}**`).join('\n')}\n\nJust type the section name or tell me what to change.`,
            });
          },
        },
        {
          id: 'download',
          label: 'Skip — Download PDF',
          variant: 'secondary',
          onClick: () => {
            proceedToFulfillment();
            window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
          },
        },
        {
          id: 'refine',
          label: 'Make a change',
          variant: 'secondary',
          onClick: () => {
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: refineSectionPrompt(),
            });
          },
        },
        {
          id: 'chip-punchier-summary',
          label: 'Make summary punchier',
          variant: 'secondary',
          onClick: () => {
            onMessage({ id: crypto.randomUUID(), role: 'user', content: 'Make the summary more concise and impactful' });
          },
        },
        {
          id: 'chip-more-metrics',
          label: 'Add more metrics',
          variant: 'secondary',
          onClick: () => {
            onMessage({ id: crypto.randomUUID(), role: 'user', content: 'Add more quantitative metrics and numbers to my experience section' });
          },
        },
      ],
    });
  }, [setPhase, onMessage, dispatch]);

  const proceedToTemplate = useCallback(() => {
    setPhase('template');

    const currentId = stateRef.current.templateId;
    const currentLabel =
      TEMPLATE_LIBRARY.find((t) => t.id === currentId)?.label ??
      currentId;

    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: `Your resume is looking great! Currently using the **${currentLabel}** template.\n\nWould you like to try a different ATS-certified design? You can also switch anytime using the dropdown in the canvas header.`,
      actions: [
        {
          id: 'keep',
          label: `Keep ${currentLabel}`,
          variant: 'primary',
          onClick: () => proceedToFollowup(),
        },
        {
          id: 'ats-optimized',
          label: 'ATS Optimized',
          variant: 'secondary',
          onClick: () => {
            dispatch({ type: 'SET_TEMPLATE', templateId: 'ats-optimized' });
            proceedToFollowup();
          },
        },
        {
          id: 'executive',
          label: 'Executive',
          variant: 'secondary',
          onClick: () => {
            dispatch({ type: 'SET_TEMPLATE', templateId: 'executive' });
            proceedToFollowup();
          },
        },
        {
          id: 'navy-tech',
          label: 'Navy Tech',
          variant: 'secondary',
          onClick: () => {
            dispatch({ type: 'SET_TEMPLATE', templateId: 'navy-tech' });
            proceedToFollowup();
          },
        },
      ],
    });
  }, [setPhase, onMessage, dispatch]);

  // ── Phase 6: Follow-up (Cover Letter) ───────────────────────────────────────
  const proceedToFollowup = useCallback(() => {
    setPhase('followup');

    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: `Your optimized resume is ready!\n\nWould you like me to draft a **cover letter** for this role too? I can tailor it specifically for ${state.job?.company || 'this company'}.`,
      actions: [
        {
          id: 'yes-cover',
          label: 'Yes, write a cover letter',
          variant: 'primary',
          onClick: () => generateCoverLetter(),
        },
        {
          id: 'no-cover',
          label: 'No, skip to download',
          variant: 'secondary',
          onClick: () => {
            proceedToFulfillment();
            window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
          },
        },
      ],
    });
  }, [setPhase, onMessage, state.job]);

  // ── Generate Application Note ───────────────────────────────────────────────
  // Short application letter — generated via ai-companion so it actually
  // adapts to the real target job/company instead of hallucinating from a
  // stale summary string.
  const generateApplicationNote = useCallback(async () => {
    const origData = stateRef.current.originalResumeData;
    const job = stateRef.current.job;

    // Build a plain-text resume snapshot from the current canvas sections so the
    // LLM has the optimized content (not the noisy original).
    const optimizedResume = (stateRef.current.sections ?? [])
      .map((s) => `## ${s.title}\n${s.content}`)
      .join('\n\n')
      .slice(0, 6000);

    dispatch({ type: 'SET_TAB', tab: 'application_note' });
    startStreaming('application_note');

    // generateLetter now handles retries + fallback internally
    let note = '';
    try {
      const result = await generateLetter({
        mode: 'application',
        resume: optimizedResume,
        jobTitle: job?.title || 'the role',
        company: job?.company || 'this company',
        locale: normalizeDocumentLocale(stateRef.current.documentLocale),
      });
      note = (result.letter || '').trim();
    } catch (err) {
      console.warn('[generateApplicationNote] LLM call failed, using fallback:', err);
    }

    if (!note || note.length < 100) {
      note = buildFallbackLetter(
        'application',
        job?.title || 'the role',
        job?.company || 'this company',
        optimizedResume,
      );
    }

    appendStream(note);
    dispatch({ type: 'SET_APPLICATION_NOTE', content: note });
    await waitForCanvasTypewriter(note);
    stopStreaming();

    if (job?.id) {
      void saveAgentArtifacts({
        jobId: String(job.id),
        jobTitle: job.title || 'Untitled role',
        companyName: job.company || 'Unknown company',
        jobUrl: (job as { url?: string } | null)?.url,
        location: (job as { location?: string } | null)?.location,
        applicationNote: note,
      });
    }

    // Offer next actions — never terminate the conversation
    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: [
        applicationNoteReadyMessage(),
        '',
        '**Your complete application package is ready!** Here\'s what you can do next:',
        '',
        '- **Download everything** as PDF — resume, cover letter & application note bundled',
        '- **Apply directly** — I can open the job posting for you',
        '- **Prepare for the interview** — practice common questions for this role',
        '- **Find similar roles** — search for more matching positions',
        '- **Refine any document** — just tell me what to change',
      ].join('\n'),
      actions: [
        {
          id: 'download-all',
          label: 'Download All Documents',
          variant: 'primary',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
          },
        },
        {
          id: 'interview-prep',
          label: 'Prepare for Interview',
          variant: 'secondary',
          onClick: () => {
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: `Let's prepare for your interview at **${job?.company || 'this company'}** for the **${job?.title || 'this role'}** position.\n\nI can help with:\n- **Mock interview questions** — role-specific behavioral & technical\n- **STAR method practice** — structure your answers\n- **Company research** — key talking points\n\nWhat would you like to start with?`,
              actions: [
                { id: 'mock-questions', label: 'Mock Interview Questions', variant: 'primary', onClick: () => { /* placeholder for interview prep navigation */ } },
                { id: 'star-practice', label: 'STAR Method Practice', variant: 'secondary', onClick: () => { /* placeholder for STAR practice navigation */ } },
              ],
            });
          },
        },
        {
          id: 'find-similar',
          label: 'Find Similar Jobs',
          variant: 'secondary',
          onClick: () => {
            dispatch({ type: 'BACK_TO_LIST' });
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: `Returning to the job list. Pick another role to optimize for, or tell me what kind of jobs you're looking for.`,
            });
          },
        },
      ],
    });
  }, [dispatch, startStreaming, appendStream, stopStreaming, onMessage]);

  // ── Generate Cover Letter (LLM-backed) ──────────────────────────────────────
  const generateCoverLetter = useCallback(async () => {
    void ledger.coverLetterGenerate({
      job_id: stateRef.current.job?.id,
      job_title: stateRef.current.job?.title,
    });

    dispatch({ type: 'SET_TAB', tab: 'cover_letter' });
    startStreaming('cover_letter');

    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: draftingCoverLetterWithTabMessage(
        stateRef.current.job?.title ?? 'this role',
        stateRef.current.job?.company ?? 'this company',
      ),
    });

    const origData = stateRef.current.originalResumeData;
    const job = stateRef.current.job;
    const candidateName = origData?.personalInfo?.fullName || origData?.name || 'Your Name';
    const optimizedResume = (stateRef.current.sections ?? [])
      .map((s) => `## ${s.title}\n${s.content}`)
      .join('\n\n')
      .slice(0, 6000);

    // Use the dedicated ai-companion letter generator (proper system prompt,
    // JWT auth, returns a real cover letter — no resume content leakage).
    // generateLetter now handles retries + fallback internally.
    let letterText = '';
    try {
      const result = await generateLetter({
        mode: 'cover',
        resume: optimizedResume,
        jobTitle: job?.title || 'the role',
        company: job?.company || 'this company',
        tone: 'professional',
        locale: normalizeDocumentLocale(stateRef.current.documentLocale),
      });
      letterText = (result.letter || '').trim();
    } catch (err) {
      console.warn('[generateCoverLetter] LLM call failed, using fallback:', err);
    }

    // Fallback if LLM returned empty/refused
    if (!letterText || letterText.length < 100) {
      letterText = buildFallbackLetter(
        'cover',
        job?.title || 'the role',
        job?.company || 'this company',
        optimizedResume,
      );
    }

    // Stream the letter into the canvas
    appendStream(letterText);
    dispatch({ type: 'SET_COVER_LETTER', content: letterText });
    await waitForCanvasTypewriter(letterText);
    stopStreaming();

    if (job?.id) {
      void saveAgentArtifacts({
        jobId: String(job.id),
        jobTitle: job.title || 'Untitled role',
        companyName: job.company || 'Unknown company',
        jobUrl: (job as { url?: string } | null)?.url,
        location: (job as { location?: string } | null)?.location,
        coverLetter: letterText,
      });
    }

    // Milestone: Cover Letter Done — offer logical next steps
    const clJob = stateRef.current.job;
    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: [
        coverLetterReadyMessage(),
        '',
        `**Done!** Your cover letter for **${clJob?.title || 'this role'}** at **${clJob?.company || 'this company'}** is ready.`,
        '',
        'Ready to prep? I can run a **mock interview** tailored specifically to this job description, or generate a quick **application note** to round out your package.',
      ].join('\n'),
      actions: [
        {
          id: 'gen-app-note',
          label: 'Generate Application Note',
          variant: 'primary',
          onClick: () => { void generateApplicationNote(); },
        },
        {
          id: 'interview-prep-cl',
          label: 'Prepare for Interview',
          variant: 'secondary',
          onClick: () => {
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: `Let's prepare for your interview at **${clJob?.company || 'this company'}**.\n\n- **Mock questions** — behavioral & technical for ${clJob?.title || 'this role'}\n- **STAR method** — structure winning answers\n- **Company research** — talking points\n\nWhat would you like?`,
              actions: [
                { id: 'mock-q-cl', label: 'Mock Interview Questions', variant: 'primary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
                { id: 'star-cl', label: 'STAR Method Practice', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
              ],
            });
          },
        },
        {
          id: 'refine-cover',
          label: 'Refine this cover letter',
          variant: 'secondary',
          onClick: () => {
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: refineCoverLetterPrompt(),
            });
          },
        },
        {
          id: 'download-pdf-cover',
          label: 'Download PDF',
          variant: 'secondary',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
          },
        },
      ],
    });
  }, [dispatch, startStreaming, appendStream, stopStreaming, onMessage, generateApplicationNote]);

  // ── Phase 7: Fulfillment ────────────────────────────────────────────────────
  // Uses milestone-based prompts: the message and actions adapt to which
  // artifacts have already been generated so the user always sees a logical
  // "next best action" and never hits a dead end.
  const proceedToFulfillment = useCallback(() => {
    setPhase('fulfillment');
    dispatch({ type: 'SET_TAB', tab: 'resume' });

    const company = state.job?.company || 'this company';
    const jobTitle = state.job?.title || 'this role';
    const hasCoverLetter = !!state.coverLetter;
    const hasAppNote = !!state.applicationNote;
    const score = stateRef.current.analysis?.matchScore;

    // ── Build milestone-aware message ──
    const lines: string[] = [];
    const actions: AgentAction[] = [];

    if (hasCoverLetter && hasAppNote) {
      // All 3 artifacts complete — "Application Package Ready" milestone
      lines.push(
        `**Your complete application package is ready!**`,
        '',
        `Resume, cover letter, and application note for **${jobTitle}** at **${company}** — all saved to **My Activities**.`,
        score ? `\nYour ATS match: **${score}%**` : '',
        '',
        `**What's next?**`,
        `- **Download & apply** — get your documents and submit your application`,
        `- **Interview prep** — practice role-specific questions before you hear back`,
        `- **Keep the pipeline full** — find 3+ similar roles so you're never waiting on one company`,
      );

      actions.push(
        {
          id: 'fulfill-download',
          label: 'Download All Documents',
          variant: 'primary',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
            // After download, nudge the user to keep going
            setTimeout(() => {
              onMessage({
                id: crypto.randomUUID(),
                role: 'assistant',
                content: `Documents downloaded! While you submit your application, I can **find 3 similar roles** to keep your pipeline healthy — or help you **prepare for the interview**. What sounds good?`,
                actions: [
                  { id: 'post-dl-similar', label: 'Find similar roles', variant: 'primary', onClick: () => { dispatch({ type: 'BACK_TO_LIST' }); } },
                  { id: 'post-dl-interview', label: 'Interview prep', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
                ],
              });
            }, 1500);
          },
        },
        {
          id: 'fulfill-apply',
          label: `Apply to ${company}`,
          variant: 'secondary',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('ggj:agent-apply'));
            setTimeout(() => {
              onMessage({
                id: crypto.randomUUID(),
                role: 'assistant',
                content: `Application tracked! Let's look at **3 other companies** hiring for similar tech stacks to keep your pipeline full.`,
                actions: [
                  { id: 'post-apply-similar', label: 'Find similar jobs', variant: 'primary', onClick: () => { dispatch({ type: 'BACK_TO_LIST' }); } },
                  { id: 'post-apply-interview', label: 'Prepare for interview', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
                ],
              });
            }, 1500);
          },
        },
        {
          id: 'fulfill-interview',
          label: 'Prepare for Interview',
          variant: 'secondary',
          onClick: () => {
            onMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: [
                `Let's get you ready for **${jobTitle}** at **${company}**.`,
                '',
                'I can help with:',
                '- **Mock interview** — role-specific behavioral & technical questions',
                '- **STAR method** — structure your answers with Situation, Task, Action, Result',
                '- **Company research** — key talking points about ' + company,
                '',
                'Which would you like to start with?',
              ].join('\n'),
              actions: [
                { id: 'mock-q', label: 'Mock Interview', variant: 'primary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
                { id: 'star-method', label: 'STAR Method Practice', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
              ],
            });
          },
        },
        {
          id: 'fulfill-similar',
          label: 'Find Similar Jobs',
          variant: 'secondary',
          onClick: () => {
            dispatch({ type: 'BACK_TO_LIST' });
            onMessage({ id: crypto.randomUUID(), role: 'assistant', content: `Let's find more roles matching your profile. Pick a job to optimize for, or tell me what kind of roles you're looking for.` });
          },
        },
      );
    } else if (hasCoverLetter) {
      // Resume + cover letter done — prompt for application note
      lines.push(
        `**Resume and cover letter are locked!**`,
        '',
        `Saved to **My Activities** for **${jobTitle}** at **${company}**.`,
        '',
        `Ready to prep? I can draft a quick **Application Note** — a concise summary recruiters love — or jump straight to **interview practice**.`,
      );

      actions.push(
        { id: 'gen-app-note', label: 'Generate Application Note', variant: 'primary', onClick: () => { void generateApplicationNote(); } },
        { id: 'fulfill-download', label: 'Download PDF', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } })); } },
        { id: 'fulfill-interview', label: 'Prepare for Interview', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-navigate', { detail: { path: '/prepare-for-interview' } })); } },
      );
    } else {
      // Resume only — prompt for cover letter
      lines.push(
        `**Your optimized resume is ready and saved!**`,
        '',
        `You're set for **${jobTitle}** at **${company}**.`,
        score ? `ATS match: **${score}%**` : '',
        '',
        `Should we **draft a cover letter** tailored to this role? It takes 30 seconds and significantly increases callback rates.`,
      );

      actions.push(
        { id: 'yes-cover', label: 'Yes, write cover letter', variant: 'primary', onClick: () => generateCoverLetter() },
        { id: 'fulfill-download', label: 'Skip — Download PDF', variant: 'secondary', onClick: () => { window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } })); } },
        { id: 'fulfill-similar', label: 'Find similar jobs', variant: 'secondary', onClick: () => { dispatch({ type: 'BACK_TO_LIST' }); onMessage({ id: crypto.randomUUID(), role: 'assistant', content: 'Pick another role to optimize for.' }); } },
      );
    }

    onMessage({
      id: crypto.randomUUID(),
      role: 'assistant',
      content: lines.filter(Boolean).join('\n'),
      actions,
    });
  }, [setPhase, dispatch, onMessage, state.job, state.coverLetter, state.applicationNote]);

  // ── Skip directly to fulfillment ────────────────────────────────────────────
  const skipToFulfillment = useCallback(() => {
    proceedToFulfillment();
  }, [proceedToFulfillment]);

  // ── Abort ───────────────────────────────────────────────────────────────────
  const abort = useCallback(() => {
    abortRef.current?.abort();
    stopStreaming();
  }, [stopStreaming]);

  return {
    phase: state.phase,
    startAnalysis,
    startOptimizationPhase,
    proceedToConsent,
    proceedToRefinement,
    proceedToTemplate,
    proceedToFollowup,
    proceedToFulfillment,
    abort,
  };
}

export default useAgentStateMachine;
